import React, { useEffect, useState } from "react";
import { ShieldCheck, ShieldAlert, RefreshCw, Database } from "lucide-react";
import { api } from "@/lib/api";

const fmt = (s) => (s ? new Date(s).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "never");
const TONE = { ok: "bg-sage/30 text-cocoa", awaiting: "bg-champagne text-cocoa/70", none: "bg-champagne text-cocoa/70", stale: "bg-gold/30 text-cocoa", missing: "bg-rose/30 text-cocoa", low: "bg-rose/30 text-cocoa", empty: "bg-rose/40 text-cocoa" };
const STATUS_LABEL = { ok: "ok", awaiting: "awaiting first member activity", none: "none yet (optional)", stale: "stale", missing: "missing", low: "below minimum", empty: "empty" };

export function AdminIpIntegrity({ compact = false }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => { setBusy(true); api.get("/admin/ip-integrity").then((r) => setD(r.data)).catch(() => setD({ error: true })).finally(() => setBusy(false)); };
  useEffect(() => { load(); }, []);

  if (!d) return <p className="text-sm text-cocoa/50">Checking local IP stores…</p>;
  if (d.error) return <p className="text-sm text-rose" data-testid="ip-integrity-error">Couldn't run the IP integrity probe.</p>;
  const Icon = d.ok ? ShieldCheck : ShieldAlert;

  const header = (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="flex items-center gap-3">
        <span className={`p-2 rounded-full ${d.ok ? "bg-sage/40" : "bg-rose/40"}`}><Icon size={18} className="text-cocoa" /></span>
        <div>
          <p className="font-medium text-cocoa" data-testid="ip-integrity-status">{d.ok ? "All intelligence IP is stored locally and actively written" : "Attention: one or more IP stores need review"}</p>
          <p className="text-xs text-cocoa/55"><Database size={11} className="inline mr-1" />{d.storage.engine} · db <code>{d.storage.db}</code> · embeddings {d.embedding_dims}-d{d.null_embeddings_queued ? ` · ${d.null_embeddings_queued} queued` : ""} · checked {fmt(d.checked_at)}</p>
        </div>
      </div>
      <button onClick={load} disabled={busy} className="ss-pill ss-pill-ghost h-9" data-testid="ip-integrity-refresh"><RefreshCw size={13} className={busy ? "animate-spin" : ""} /> Re-check</button>
    </div>
  );

  const reasons = d.reasons?.length > 0 && (
    <ul className="mt-3 space-y-1 text-sm text-cocoa/80" data-testid="ip-integrity-reasons">
      {d.reasons.map((r, i) => <li key={i} className="flex gap-2"><span className="text-rose">•</span><span>{r}</span></li>)}
    </ul>
  );

  if (compact) return <div className="ss-card p-4" data-testid="ip-integrity-compact">{header}{reasons}{!d.ok && <p className="mt-2 text-xs text-cocoa/50">Full breakdown under Operations → Continuity &amp; IP.</p>}</div>;

  return (
    <div className="ss-card p-5 space-y-4" data-testid="ip-integrity">
      {header}
      {reasons}
      {d.latest_session_chain && (
        <div className="bg-ivory border border-champagne/70 rounded-lg p-3 text-sm text-cocoa/75" data-testid="ip-integrity-chain">
          <p className="text-[10px] uppercase tracking-widest text-cocoa/50">Latest advisor session → full IP chain</p>
          <div className="mt-1.5 flex flex-wrap gap-2">
            {[["embedding", "Vector memory"], ["events", "Relationship events"], ["shadow_match", "Ontology match"], ["usage_event", "Cost ledger"]].map(([k, l]) => (
              <span key={k} className={`px-2 py-0.5 rounded-full text-xs ${d.latest_session_chain[k] ? "bg-sage/30" : "bg-rose/30"}`}>{d.latest_session_chain[k] ? "✓" : "✗"} {l}</span>
            ))}
            <span className="text-xs text-cocoa/45 self-center">{fmt(d.latest_session_chain.created_at)}</span>
          </div>
        </div>
      )}
      <table className="w-full text-sm ss-table-stack" data-testid="ip-integrity-table">
        <thead className="text-[10px] uppercase tracking-widest text-cocoa/50"><tr><th className="text-left py-1">Store</th><th className="text-right py-1">Records</th><th className="text-right py-1">Last write</th><th className="text-right py-1">Status</th></tr></thead>
        <tbody>{d.stores.map((s) => (
          <tr key={s.collection} className="border-t border-champagne/60 text-cocoa" data-testid={`ip-store-${s.collection}`}>
            <td className="py-1.5 font-medium sm:font-normal">{s.label}<span className="text-xs text-cocoa/40 ml-1.5">{s.collection}</span></td>
            <td data-label="Records" className="text-right">{s.count.toLocaleString()}</td>
            <td data-label="Last write" className="text-right text-cocoa/65">{fmt(s.last_write_at)}</td>
            <td data-label="Status" className="text-right">
              <span className={`px-2 py-0.5 rounded-full text-xs ${TONE[s.status] || TONE.ok}`}>{STATUS_LABEL[s.status] || s.status}</span>
              {s.eligible_reads_14d !== undefined && (
                <span className="block mt-1 text-[11px] text-cocoa/50" data-testid={`ip-eligible-${s.collection}`} title={s.note}>
                  {s.eligible_reads_14d} of {s.total_reads_14d} reads eligible (Deep Memory + profile)
                </span>
              )}
            </td>
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}
